import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { phoneRegExp } from "@/utils/regex";
import styles from "../../styles/shap_of_you.module.scss";
import { SignupScreenProps } from "./types";

const SignupScreen: React.FC<SignupScreenProps> = ({
  navigateToStep,
  authData,
  logStep,
}) => {
  const [sendOtpLoading, setSendOtpLoading] = useState(false);

  // Register Formik
  const registerFormik = useFormik({
    initialValues: {
      firstName: authData.registerData.firstName || "",
      lastName: authData.registerData.lastName || "",
      email: authData.registerData.email || "",
      mobile: authData.mobileNumber || authData.registerData.mobile || "",
      pincode: authData.registerData.pincode || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().trim().required("First name is required"),
      lastName: Yup.string().trim().required("Last name is required"),
      email: Yup.string()
        .required("Email is required")
        .email("Email is invalid"),
      mobile: Yup.string()
        .matches(phoneRegExp, "Mobile number is not valid")
        .test(
          "len",
          "Mobile number needs to be exactly 10 digits",
          (val: any) => val?.toString().length === 10
        )
        .required("Mobile is required"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^\d{6}$/, "Invalid pincode. Must be a 6-digit number"),
    }),
    onSubmit: async (values) => {
      setSendOtpLoading(true);
      const apiEndpoint = endpoints.auth.sendOtp();
      const requestPayload = {
        mobile: values.mobile,
        email: values.email,
        pincode: values.pincode,
      };

      try {
        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful OTP send (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: 200,
            response_payload: { otp_uuid: data.otp_uuid },
            success_message: data.message,
          });
        }

        // Success - OTP sent for registration
        authData.setOtpUid(data.otp_uuid);
        authData.setMobileNumber(values.mobile);
        authData.setAuthFlow("register");
        authData.setRegisterData({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          mobile: values.mobile,
          pincode: values.pincode,
        });

        toast(data.message, {
          type: "success",
          toastId: "auth-otp-success",
        });

        navigateToStep("verifyOtp");
      } catch (error: any) {
        // Log API error (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: error.response?.status || 500,
            error_message: error.response?.data?.message || "Failed to send OTP",
            error_code: error.response?.status?.toString() || "500",
          });
        }

        if (error.response?.data?.message) {
          toast(error.response.data.message, {
            type: "error",
            toastId: "auth-otp-error",
          });
        } else {
          toast("Something went wrong! Try again!", {
            type: "error",
            toastId: "auth-otp-error",
          });
        }
      }
      setSendOtpLoading(false);
    },
  });

  // Update mobile number in formik when it comes from authData
  useEffect(() => {
    if (authData.mobileNumber && !registerFormik.values.mobile) {
      registerFormik.setFieldValue("mobile", authData.mobileNumber);
    }
  }, [authData.mobileNumber]);

  const isFormComplete =
    registerFormik.values.firstName &&
    registerFormik.values.lastName &&
    registerFormik.values.email &&
    registerFormik.values.mobile &&
    registerFormik.values.pincode &&
    registerFormik.isValid;

  return (
    <div className="d-md-none w-100">
      <div className={styles.wrappersignup}>
        {/* Header */}
        <h1>Enter Your Details</h1>
        <p>Let us get to know you better</p>

        {/* Form */}
        <form className={styles.form} onSubmit={registerFormik.handleSubmit}>
          <div>
            {/* First Name */}
            <div className={styles.inputBox}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4001_1148)">
                  <path
                    d="M8 7C8 8.06087 8.42143 9.07828 9.17157 9.82843C9.92172 10.5786 10.9391 11 12 11C13.0609 11 14.0783 10.5786 14.8284 9.82843C15.5786 9.07828 16 8.06087 16 7C16 5.93913 15.5786 4.92172 14.8284 4.17157C14.0783 3.42143 13.0609 3 12 3C10.9391 3 9.92172 3.42143 9.17157 4.17157C8.42143 4.92172 8 5.93913 8 7Z"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4001_1148">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <input
                type="text"
                placeholder="First Name"
                value={registerFormik.values.firstName}
                onChange={(e) =>
                  registerFormik.setFieldValue("firstName", e.target.value)
                }
                onBlur={registerFormik.handleBlur}
              />
            </div>
            {registerFormik.touched.firstName &&
            registerFormik.errors.firstName ? (
              <small
                className="text-danger"
                style={{ display: "block", marginTop: "5px" }}
              >
                {registerFormik.errors.firstName}
              </small>
            ) : null}

            {/* Last Name */}
            <div className={styles.inputBox}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4001_1149)">
                  <path
                    d="M8 7C8 8.06087 8.42143 9.07828 9.17157 9.82843C9.92172 10.5786 10.9391 11 12 11C13.0609 11 14.0783 10.5786 14.8284 9.82843C15.5786 9.07828 16 8.06087 16 7C16 5.93913 15.5786 4.92172 14.8284 4.17157C14.0783 3.42143 13.0609 3 12 3C10.9391 3 9.92172 3.42143 9.17157 4.17157C8.42143 4.92172 8 5.93913 8 7Z"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4001_1149">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <input
                type="text"
                placeholder="Last Name"
                value={registerFormik.values.lastName}
                onChange={(e) =>
                  registerFormik.setFieldValue("lastName", e.target.value)
                }
                onBlur={registerFormik.handleBlur}
              />
            </div>
            {registerFormik.touched.lastName &&
            registerFormik.errors.lastName ? (
              <small
                className="text-danger"
                style={{ display: "block", marginTop: "5px" }}
              >
                {registerFormik.errors.lastName}
              </small>
            ) : null}

            {/* Phone (Pre-filled) */}
            <div className={`${styles.inputBox} ${styles.disabled}`}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21C14.0993 20.763 10.4202 19.1065 7.65683 16.3432C4.8935 13.5798 3.23705 9.90074 3 6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4Z"
                  stroke="#858585"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span>+91</span>
              <div className={styles.divider}></div>
              <input
                type="text"
                value={registerFormik.values.mobile}
                disabled
              />
            </div>

            {/* Email */}
            <div className={styles.inputBox}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H19C19.5304 5 20.0391 5.21071 20.4142 5.58579C20.7893 5.96086 21 6.46957 21 7V17C21 17.5304 20.7893 18.0391 20.4142 18.4142C20.0391 18.7893 19.5304 19 19 19H5C4.46957 19 3.96086 18.7893 3.58579 18.4142C3.21071 18.0391 3 17.5304 3 17V7Z"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 7L12 13L21 7"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <input
                type="email"
                placeholder="E-Mail ID"
                value={registerFormik.values.email}
                onChange={(e) =>
                  registerFormik.setFieldValue("email", e.target.value)
                }
                onBlur={registerFormik.handleBlur}
              />
            </div>
            {registerFormik.touched.email && registerFormik.errors.email ? (
              <small
                className="text-danger"
                style={{ display: "block", marginTop: "5px" }}
              >
                {registerFormik.errors.email}
              </small>
            ) : null}

            {/* Pincode */}
            <div className={styles.inputBox}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12Z"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12Z"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 2V4"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 20V22"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 12H22"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12H4"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <input
                type="number"
                placeholder="Pincode"
                maxLength={6}
                value={registerFormik.values.pincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 6) {
                    registerFormik.setFieldValue("pincode", value);
                  }
                }}
                onBlur={registerFormik.handleBlur}
              />
            </div>
            {registerFormik.touched.pincode && registerFormik.errors.pincode ? (
              <small
                className="text-danger"
                style={{ display: "block", marginTop: "5px" }}
              >
                {registerFormik.errors.pincode}
              </small>
            ) : null}

            {/* Already registered */}
            <p style={{ textAlign: "center", marginTop: "16px", marginBottom: "0", fontSize: "14px", color: "#262121" }}>
              Already a registered user?{" "}
              <span
                onClick={() => navigateToStep("login")}
                style={{ color: "#C9302C", cursor: "pointer", fontWeight: 600 }}
              >
                Login
              </span>
            </p>
          </div>

          {/* Button */}
          <button
            className={styles.btn}
            type="submit"
            disabled={sendOtpLoading || !isFormComplete}
            style={
              sendOtpLoading || !isFormComplete
                ? {
                    background: "#d3d3d3",
                    color: "#888",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }
                : {
                    background: "#C9302C",
                    color: "white",
                    cursor: "pointer",
                  }
            }
          >
            {sendOtpLoading ? "Sending..." : "Send OTP"} <span>→</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupScreen;
